// When a tab should hold a person's edit lock (TGCLOUD-1669 Phase 8).
//
// Pure, so the rule with real consequences is pinned by tests rather than only
// being reachable by mounting a 1400-line component in jsdom. The same shape as
// familyLoadPolicy and editSignalPolicy beside it.
//
// The standing principle is at most ONE edit lock in normal use, and it has to
// survive several patients being open. The tab strip is meant to be a truthful
// map of who holds what, which it can only be if the rule is this simple.

/**
 * What this tab should be doing about the person lock.
 *
 * @param {boolean} isActive        is this the active tab
 * @param {boolean} hasUnsavedWork  does this tab have unsaved edits
 * @param {boolean} isHolding       does this tab currently hold the lock
 *
 * @returns {'hold'|'release-after-idle'|'idle'}
 *   'hold'                keep it, and keep heartbeating
 *   'release-after-idle'  let it go once the idle window passes
 *   'idle'                nothing to do; this tab holds nothing
 */
export const personLockIntent = ({ isActive, hasUnsavedWork, isHolding }) => {
  // The active tab holds it. Nothing else competes.
  if (isActive) return 'hold';

  // THE EXCEPTION, and the reason this is not a one-liner. A background tab
  // with unsaved work keeps its lock: releasing one out from under an unsaved
  // edit is how you lose the edit. The user is told why, because otherwise a
  // colleague is locked out with no explanation.
  if (hasUnsavedWork) return 'hold';

  // Backgrounded, saved, and holding: let it go, but not instantly. The idle
  // window is what makes flicking between tabs cheap.
  if (isHolding) return 'release-after-idle';

  return 'idle';
};

/** Convenience for the acquire/heartbeat effect. */
export const shouldHoldPersonLock = (state) =>
  personLockIntent(state) === 'hold';

/**
 * Whether to offer the inline "Take editing lock" affordance.
 *
 * TGCLOUD-1669 Phase 8. The plan's line is that clicking into a field on a
 * view-only patient must show a way to take the lock "rather than failing
 * silently", and one specific state was doing exactly that: the FAMILY is
 * locked by a colleague, this user holds no person lock, so every section is
 * disabled with no banner and no explanation. A person lock overrides a family
 * lock for that person's own fields, so the lock was available the whole time
 * and there was nothing on screen that said so.
 *
 * Offered as a persistent banner rather than on field-click, deliberately. An
 * affordance that appears only after you click something that did nothing has
 * already failed once by the time it helps.
 *
 * The four exclusions each have a different reason, which is why this is a
 * function and not a boolean expression inline:
 *
 *  - `lockedByOther`   SidebarLockBanner already names the holder and offers
 *                      Request access and Force acquire. A second banner
 *                      offering a third route would compete with it.
 *  - `hasPersonLock`   already editable; there is nothing to take.
 *  - `isSecondaryRecord` / `bulkEditActive`
 *                      read-only for reasons that are not a lock, so taking one
 *                      would change nothing. Offering it would be a lie.
 *
 * @returns {'offer'|'none'}
 */
export const takeLockIntent = ({
  lockedByOther,
  hasPersonLock,
  pedigreeLockedByOther,
  isSecondaryRecord,
  bulkEditActive
}) => {
  if (lockedByOther) return 'none';
  if (hasPersonLock) return 'none';
  if (isSecondaryRecord || bulkEditActive) return 'none';
  return pedigreeLockedByOther ? 'offer' : 'none';
};
